import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Badge,
  Card,
  Heading,
  SimpleGrid,
  Alert,
  Input,
  Textarea,
  Field,
} from '@chakra-ui/react';
import { FiFile, FiShield, FiCheck, FiClock } from 'react-icons/fi';
import { CreateReviewData } from '../../pages/CreateReviewPage';
import { useTranslation } from 'react-i18next';
import { getAssessmentUnitLabels } from '../../utils/frameworkTerminology';

const { Body: CardBody, Header: CardHeader } = Card;

interface ReviewSummaryStepProps {
  reviewData: CreateReviewData;
  onReviewDataUpdate: (updates: Partial<CreateReviewData>) => void;
}

const ReviewSummaryStep: React.FC<ReviewSummaryStepProps> = ({
  reviewData,
  onReviewDataUpdate,
}) => {
  const { t, i18n } = useTranslation();
  const terminology = getAssessmentUnitLabels(
    reviewData.selectedStandard?.standard_type,
    reviewData.selectedSections,
    i18n.language
  );

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return t('createReview.steps.zeroBytes');
    const k = 1024;
    const sizes = [
      t('createReview.steps.bytes'),
      t('createReview.steps.kilobytes'),
      t('createReview.steps.megabytes'),
      t('createReview.steps.gigabytes'),
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getEvidenceCount = () => {
    return reviewData.evidenceFiles.length;
  };

  const getAiLinkedCount = () => {
    return reviewData.aiLinkedEvidence.reduce((total, link) => total + link.evidenceIds.length, 0);
  };

  const getManualLinkedCount = () => {
    return reviewData.manualEvidenceLinks.reduce((total, link) => total + link.evidenceIds.length, 0);
  };

  const formatSectionHeading = (sectionId: string) => {
    if (sectionId.startsWith('goal-')) {
      const goalNo = sectionId.split('-')[1] || '';
      return `الغاية ${goalNo}`;
    }
    return sectionId;
  };

  // Build display items: show specific subsections when selected, otherwise show parent section
  const getSectionDisplayItems = () => {
    const items: { id: string; label: string; isSubsection: boolean }[] = [];
    
    for (const sectionId of reviewData.selectedSections) {
      const subsections = reviewData.selectedSubsections?.[sectionId];
      if (subsections && subsections.length > 0) {
        // Show individual subsections instead of parent
        for (const subsectionKey of subsections) {
          // subsectionKey format is "6.1.1:When planning for..."
          const [subId, ...titleParts] = subsectionKey.split(':');
          const title = titleParts.join(':');
          items.push({
            id: `${sectionId}-${subId}`,
            label: title ? `${subId} - ${title}` : subId,
            isSubsection: true,
          });
        }
      } else {
        // No subsections selected — show the parent section
        items.push({
          id: sectionId,
          label: formatSectionHeading(sectionId),
          isSubsection: false,
        });
      }
    }
    
    return items;
  };

  const estimatedTime = Math.max(30, reviewData.selectedSections.length * 5 + reviewData.evidenceFiles.length * 2);

  return (
    <VStack gap={6} align="stretch">
      <Box>
        <Heading size="md" mb={2}>
          {t('createReview.steps.assessmentSummarySubmissionTitle')}
        </Heading>
        <Text color="gray.600">
          {t('createReview.steps.assessmentSummarySubmissionSubtitle')}
        </Text>
      </Box>

      {/* Review Configuration Summary */}
      <Card.Root variant="outline">
        <CardHeader>
          <Heading size="sm">{t('createReview.steps.assessmentConfiguration')}</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
            {/* Policy Information */}
            <VStack gap={4} align="stretch">
              <HStack gap={3}>
                <Icon as={FiFile} w={5} h={5} color="brand.500" />
                <VStack align="start" gap={1}>
                  <Text fontWeight="semibold">{t('createReview.steps.policyDocument')}</Text>
                  <Text fontSize="sm" color="gray.600">
                    {reviewData.policy?.name || t('createReview.steps.noDocumentSelected')}
                  </Text>
                  {reviewData.policy?.file_size && (
                    <Text fontSize="xs" color="gray.500">
                      {formatFileSize(reviewData.policy.file_size)}
                    </Text>
                  )}
                </VStack>
              </HStack>

              {/* Standard Information */}
              <HStack gap={3}>
                <Icon as={FiShield} w={5} h={5} color="green.500" />
                <VStack align="start" gap={1}>
                  <Text fontWeight="semibold">{t('createReview.steps.complianceStandard')}</Text>
                  <Text fontSize="sm" color="gray.600">
                    {reviewData.selectedStandard?.name || t('createReview.steps.noStandardSelected')}
                  </Text>
                  {reviewData.selectedStandard?.version && (
                    <Text fontSize="xs" color="gray.500">
                      {t('createReview.steps.versionLabel', { version: reviewData.selectedStandard.version })}
                    </Text>
                  )}
                </VStack>
              </HStack>
            </VStack>

            {/* Review Details */}
            <VStack gap={4} align="stretch">
              <Box>
                <Text fontWeight="semibold" mb={2}>{t('createReview.steps.assessmentScope')}</Text>
                <VStack align="start" gap={1}>
                  <HStack gap={2}>
                    <Badge colorPalette="blue" variant="subtle">
                      {t('createReview.steps.sectionsCount', {
                        count: getSectionDisplayItems().length,
                        unitPlural: terminology.plural,
                      })}
                    </Badge>
                    {reviewData.customSections.length > 0 && (
                      <Badge colorPalette="purple" variant="subtle">
                        {t('createReview.steps.customCount', { count: reviewData.customSections.length })}
                      </Badge>
                    )}
                  </HStack>
                  <Text fontSize="sm" color="gray.600">
                    {reviewData.selectedTemplate?.name || t('createReview.steps.customSectionSelection', { unitSingular: terminology.singular, unitPlural: terminology.plural })}
                  </Text>
                </VStack>
              </Box>

              <Box>
                <Text fontWeight="semibold" mb={2}>{t('createReview.steps.evidenceAndLinks')}</Text>
                <VStack align="start" gap={1}>
                  <HStack gap={2}>
                    <Badge colorPalette="green" variant="subtle">
                      {t('createReview.steps.filesCount', { count: getEvidenceCount() })}
                    </Badge>
                    {getAiLinkedCount() > 0 && (
                      <Badge colorPalette="blue" variant="subtle">
                        {t('createReview.steps.aiLinkedCount', { count: getAiLinkedCount() })}
                      </Badge>
                    )}
                    {getManualLinkedCount() > 0 && (
                      <Badge colorPalette="orange" variant="subtle">
                        {t('createReview.steps.manualCount', { count: getManualLinkedCount() })}
                      </Badge>
                    )}
                  </HStack>
                </VStack>
              </Box>
            </VStack>
          </SimpleGrid>
        </CardBody>
      </Card.Root>

      {/* Pricing and Timeline */}
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
        {/* Timeline Card */}
        <Card.Root variant="elevated" bg="brand.50">
          <CardHeader>
            <HStack gap={2}>
              <Icon as={FiClock} w={5} h={5} color="brand.500" />
              <Heading size="sm">{t('createReview.steps.estimatedTimeline')}</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack gap={3} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="sm">{t('createReview.steps.estimatedCompletion')}</Text>
                <Text fontWeight="semibold">{t('createReview.steps.minutesShort', { count: estimatedTime })}</Text>
              </HStack>
              <Text fontSize="xs" color="gray.500">
                {t('createReview.steps.actualTimeMayVary')}
              </Text>
            </VStack>
          </CardBody>
        </Card.Root>
      </SimpleGrid>

      {/* Review Details Form */}
      <Card.Root variant="outline">
        <CardHeader>
          <Heading size="sm">{t('createReview.steps.assessmentDetails')}</Heading>
        </CardHeader>
        <CardBody>
          <VStack gap={4} align="stretch">
            <Field.Root required>
              <Field.Label>{t('createReview.steps.assessmentName')}</Field.Label>
              <Input
                placeholder={t('createReview.steps.assessmentNamePlaceholder')}
                value={reviewData.reviewName}
                onChange={(e) => onReviewDataUpdate({ reviewName: e.target.value })}
                bg="white"
              />
              <Field.HelperText>
                {t('createReview.steps.assessmentNameHelper')}
              </Field.HelperText>
            </Field.Root>

            <Field.Root>
              <Field.Label>{t('createReview.steps.assessmentDescriptionOptional')}</Field.Label>
              <Textarea
                placeholder={t('createReview.steps.assessmentDescriptionPlaceholder')}
                value={reviewData.reviewDescription}
                onChange={(e) => onReviewDataUpdate({ reviewDescription: e.target.value })}
                rows={3}
                bg="white"
              />
              <Field.HelperText>
                {t('createReview.steps.assessmentDescriptionHelper')}
              </Field.HelperText>
            </Field.Root>

            <Field.Root>
              <Field.Label>{t('createReview.steps.assessmentLanguage')}</Field.Label>
              <select
                value={reviewData.language}
                onChange={(e) => onReviewDataUpdate({ language: e.target.value })}
                style={{
                  background: 'white',
                  border: '1px solid #E2E8F0',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  width: '100%',
                }}
              >
                <option value="en">{t('createReview.steps.languageEnglish')}</option>
                <option value="ar">{t('createReview.steps.languageArabic')}</option>
              </select>
              <Field.HelperText>
                {t('createReview.steps.assessmentLanguageHelper')}
              </Field.HelperText>
            </Field.Root>
          </VStack>
        </CardBody>
      </Card.Root>

      {/* Selected Sections Preview */}
      {reviewData.selectedSections.length > 0 && (
        <Card.Root variant="outline">
          <CardHeader>
            <Heading size="sm">
              {t('createReview.steps.assessmentSectionsCount', {
                count: getSectionDisplayItems().length,
                unitTitlePlural: terminology.titlePlural,
              })}
            </Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={3}>
              {getSectionDisplayItems().map((item) => (
                <HStack key={item.id} gap={2} p={2} bg={item.isSubsection ? 'blue.50' : 'gray.50'} borderRadius="md">
                  <Icon as={FiCheck} w={4} h={4} color="green.500" />
                  <Text fontSize="sm" fontWeight="medium">
                    {item.label}
                  </Text>
                </HStack>
              ))}
            </SimpleGrid>
          </CardBody>
        </Card.Root>
      )}

      {/* Custom Sections */}
      {reviewData.customSections.length > 0 && (
        <Card.Root variant="outline">
          <CardHeader>
            <Heading size="sm">
              {t('createReview.steps.customSectionsCount', {
                count: reviewData.customSections.length,
                unitPlural: terminology.plural,
              })}
            </Heading>
          </CardHeader>
          <CardBody>
            <VStack gap={2} align="stretch">
              {reviewData.customSections.map((section, index) => (
                <Box key={index} p={3} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200">
                  <Text fontSize="sm">{section}</Text>
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card.Root>
      )}

      {/* Submission Alert */}
      <Alert.Root status="info" borderRadius="md">
        <Alert.Indicator>
          <Icon as={FiCheck} />
        </Alert.Indicator>
        <Box>
          <Alert.Title>{t('createReview.steps.readyToSubmit')}</Alert.Title>
          <Alert.Description>
            {t('createReview.steps.readyToSubmitDescription')}
          </Alert.Description>
        </Box>
      </Alert.Root>

      {/* Terms Text */}
      <Text fontSize="sm" color="gray.500" textAlign="center" mt={2}>
        {t('createReview.steps.termsAgreement')}
      </Text>
    </VStack>
  );
};

export default ReviewSummaryStep; 
